import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { type AudienceGroupedOptionType } from '@/api/audience';
import { usePixelImpressionTrend } from '@/api/pixel';
import Flare, { Area, Axis, Legend, Notes, Tooltip } from '@/components/Flare';
import { usePageFilterContext } from '@/components/page';
import { NotesPanel } from '@/components/panels';
import { ALL_PRIMARY } from '@/constants/colors';
import { MonthDateShortFormat, MonthDateYearFormat } from '@/constants/formats';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  pixelIds?: string[];
  audience?: AudienceGroupedOptionType;
};

const PixelImpressionTrendChart = ({ pixelIds, audience }: Props) => {
  const { pageFilters } = usePageFilterContext();
  const { startDate, endDate } = pageFilters;
  const audienceIds = audience?.audienceIds;

  const {
    pixelImpressionTrend,
    isLoading: isEfficiencyDataLoading,
    error,
  } = usePixelImpressionTrend(pixelIds, startDate, endDate, audienceIds);

  const seriesData = useMemo(() => {
    if (!pixelImpressionTrend?.data) {
      return undefined;
    }

    return [
      {
        id: 'impressions',
        zIndex: 1,
        name: t`Total impressions`,
        data: pixelImpressionTrend.data.map((d) => ({
          amount: d.impressions,
          date: d.sundayOfWeek,
        })),
      },
    ];
  }, [pixelImpressionTrend]);

  return (
    <NotesPanel
      startDate={startDate}
      endDate={endDate}
      title={<Trans>Impression Trend</Trans>}
      noPadding
      style={{ height: 465 }}
    >
      <Flare
        data={seriesData}
        description={t`A chart showing total impressions over time for the pixel.`}
        colors={[ALL_PRIMARY]}
        isLoading={isEfficiencyDataLoading}
        error={error}
      >
        <Legend reversed noToggleVisibility />
        <Area x="date" y="amount" position="overlap" />
        <Axis
          position="left"
          title={t`Impressions`}
          labelFormat={(point) => numberFormat(point.value, { compact: true })}
        />
        <Axis
          type="datetime"
          crosshair="line"
          position="bottom"
          labelFormat={(point) => dayjs(point.value).utc().format(MonthDateShortFormat)}
        />
        <Tooltip
          titleFormat={(point) => dayjs(point?.x).utc().format(MonthDateYearFormat)}
          rowValueFormat={(point) => numberFormat(point?.y)}
          rowSecondaryValueFormat={(point) =>
            point?.colorIndex !== 0
              ? `(${numberFormat((point?.y ?? 0) / point?.custom?.all, {
                  isPercent: true,
                  precision: 1,
                })})`
              : ''
          }
          shared
        />
        <Notes />
      </Flare>
    </NotesPanel>
  );
};

export default PixelImpressionTrendChart;
